// The Venue profile group (spec/13 §13; #403): the venue record and the registry's `venueProfile`
// settings, drawn by the generic renderer as one group with one Save.
//
//  - Editable only when `GET /v1/me/access` says the member holds `venues:update` (rule 1). While
//    that is loading, or if it cannot be read, nothing is offered for editing; the server's `403`
//    stays the enforcement either way (spec/13 §1).
//  - A `412` reloads the venue and shows the fresh values beside the editor's (rule 2) — the
//    renderer's conflict flow, the same for a venue as for a setting.
//  - Photos and the logo upload straight to storage and attach with the save (rule 3; the logo, F-ORG-01).
//  - Timezone and business-day cutoff change only in `setup`, and say so (rule 4).
//  - The venue's status heads the group: Go live from `setup`, then Pause and Resume (`VenueStatus`, spec/13 §13),
//    each with the record's own `If-Match`, so the form below follows the version it wrote.
//
// The registry's settings in this group keep their own permission (`editPermission`), which the
// server has already applied to each entry's `editable`; `venues:update` is about the record.

import type { SettingsView } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, type ReactNode } from 'react';

import { useApi } from '../api/client';
import type { MyVenue } from '../api/me';
import { queryKeys, useAccess } from '../api/queries';
import type { Versioned } from '../api/versioned';
import { Loading } from '../shell/layouts';
import { LoadFailure } from '../shell/LoadFailure';
import { useCurrentVenue } from '../shell/scope';
import { useRegistryLabels, useSettingLabels } from '../settings/labels';
import { registryPart } from '../settings/registry-source';
import { SettingsForm } from '../settings/SettingsForm';
import type { SettingsPart } from '../settings/types';
import { HolidaysControl } from './HolidaysControl';
import { LogoControl } from './LogoControl';
import { PhotosControl, PhotoUploaderProvider, type PhotoUploader } from './PhotosControl';
import { photoWhenReady, uploadImage, uploadVenuePhoto } from './uploads';
import { readVenue, venuePart, type VenueField } from './venue-source';
import { VenueStatus } from './VenueStatus';

/** The permission that edits the venue record (spec/07 §6.3; the route's `x-clavian-permission`). */
export const VENUES_UPDATE = 'venues:update';

const CUSTOM_CONTROLS = { photos: PhotosControl, logo: LogoControl, holidays: HolidaysControl };

export function VenueProfileGroup({
  organizationId,
  venueId,
  settings,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  /** The venue's settings screen, when it has loaded; its `venueProfile` entries join the group. */
  readonly settings: Versioned<SettingsView> | undefined;
}): ReactNode {
  const { t } = useTranslation('console');
  const api = useApi();
  const queryClient = useQueryClient();
  const access = useAccess({ venueId });
  const current = useCurrentVenue();
  const navigate = useNavigate();
  const venueKey = queryKeys.venue(organizationId, venueId);
  const venue = useQuery({
    queryKey: venueKey,
    queryFn: () => readVenue(api, organizationId, venueId),
    refetchOnWindowFocus: false,
  });

  const venueLabels = useSettingLabels({
    field: 'venueProfile.label',
    item: 'venueProfile.item',
    description: 'venueProfile.description',
    option: 'venueProfile.option',
  });
  const registryLabels = useRegistryLabels();
  const formatter = useVenueFormatter();
  const canEdit = access.status === 'ready' && access.can(VENUES_UPDATE);

  const uploader = useMemo<PhotoUploader>(
    () => ({
      upload: (file) => uploadVenuePhoto(api, organizationId, venueId, file),
      whenReady: (fileId, signal) => photoWhenReady(api, organizationId, fileId, { signal }),
      uploadLogo: (file) => uploadImage(api, organizationId, { purpose: 'venue_logo', venueId }, file),
    }),
    [api, organizationId, venueId],
  );

  const parts = useMemo<SettingsPart[] | undefined>(() => {
    if (venue.data === undefined) return undefined;
    const record = venuePart({
      api,
      organizationId,
      venueId,
      venue: venue.data,
      canEdit,
      words: {
        label: (field: VenueField) => venueLabels.field(field, []),
        section: (section) => t(`venueProfile.section.${section}`),
        setupOnly: t('venueProfile.setupOnly'),
        cutoffWhileTrading: (record) => {
          const last = record.businessDayCutoffHistory.at(-1);
          const rule = t('venueProfile.cutoffWhileTrading');
          if (last === undefined) return rule;
          const from = new Date(Date.parse(`${last.until}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10);
          return `${rule} ${t('venueProfile.cutoffChanged', { cutoff: record.businessDayCutoff, from, previous: last.cutoff })}`;
        },
      },
      onVersion: (next) => {
        queryClient.setQueryData(venueKey, next);
        // The venue's name and slug are also what the switcher lists and what the URL says.
        queryClient.setQueryData<readonly MyVenue[]>(queryKeys.myVenues, (venues) =>
          venues?.map((known) => (known.id === venueId ? { ...known, name: next.data.name, slug: next.data.slug } : known)),
        );
        if (next.data.slug !== current.slug) {
          void navigate({ to: '/v/$venueSlug/settings/$group', params: { venueSlug: next.data.slug, group: 'venue' }, replace: true });
        }
      },
    });
    if (settings === undefined) return [record];
    const registry = registryPart({
      api,
      scope: { kind: 'venue', organizationId, venueId },
      // The Venue group's registry rows (the closing-time rule), drawn under the record's own sub-headings.
      group: 'venue',
      view: settings,
      words: { label: (key) => registryLabels.field(key, []) },
      formatter,
      onVersion: (next) => queryClient.setQueryData(queryKeys.venueSettings(organizationId, venueId), next),
    });
    return registry.entries.length === 0 ? [record] : [record, withSection(registry, t('venueProfile.section.closing'))];
    // `venueKey` is derived from the two ids already listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venue.data, settings, canEdit, api, organizationId, venueId, venueLabels, registryLabels, formatter, queryClient, t, current.slug, navigate]);

  // What was read stays on screen if a refetch fails; only a first read that failed is an error.
  if (venue.data === undefined) {
    return venue.isError ? <LoadFailure error={venue.error} onRetry={() => void venue.refetch()} /> : <Loading />;
  }
  if (access.status === 'loading' || parts === undefined) return <Loading />;

  const labels = labelsFor(venueLabels, registryLabels);
  const notice =
    access.status === 'unavailable' ? t('venueProfile.accessUnavailable') : canEdit ? undefined : t('venueProfile.readOnly');

  return (
    <PhotoUploaderProvider value={uploader}>
      {/* Setting up, live or paused — and Go live, Pause or Resume with `venues:update` (spec/13 §13). */}
      <VenueStatus
        organizationId={organizationId}
        venueId={venueId}
        venue={venue.data}
        canEdit={canEdit}
        onVersion={(next) => queryClient.setQueryData(venueKey, next)}
        setupLink={{ to: '/v/$venueSlug/setup', params: { venueSlug: venue.data.data.slug } }}
      />
      {/* Keyed by the venue: another venue's page is another form, never this one's draft. */}
      <SettingsForm key={venueId} parts={parts} labels={labels} custom={CUSTOM_CONTROLS} notice={notice} />
    </PhotoUploaderProvider>
  );
}

/**
 * The registry part's entries keep their own save and reload, and read under one sub-heading of the record's form —
 * not the settings layout's sections, which this form does not draw.
 */
function withSection(part: SettingsPart, section: string): SettingsPart {
  const sectioned = (entries: SettingsPart['entries']) =>
    entries.map(({ sectionId: _layout, advanced: _advanced, ...entry }) => ({ ...entry, section }));
  return {
    ...part,
    entries: sectioned(part.entries),
    save: async (changes, etag) => {
      const next = await part.save(changes, etag);
      return { ...next, data: sectioned(next.data) };
    },
    reload: async () => {
      const next = await part.reload();
      return { ...next, data: sectioned(next.data) };
    },
  };
}

/** Venue fields read the venue's copy; registry settings (their keys have a dot) read the registry's. */
function labelsFor(venue: ReturnType<typeof useSettingLabels>, registry: ReturnType<typeof useSettingLabels>) {
  const pick = (entryKey: string) => (entryKey.includes('.') ? registry : venue);
  return {
    field: (entryKey: string, path: readonly string[]) => pick(entryKey).field(entryKey, path),
    description: (entryKey: string, path: readonly string[], schema: Record<string, unknown>) =>
      pick(entryKey).description(entryKey, path, schema),
    option: (entryKey: string, path: readonly string[], value: string | number | boolean) => pick(entryKey).option(entryKey, path, value),
  };
}
